import { Link } from 'react-router-dom';


function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 id={id} className="text-lg font-bold text-gray-900 mb-3 scroll-mt-24">{title}</h2>
      <div className="space-y-3 text-sm text-gray-600 leading-relaxed">{children}</div>
    </section>
  );
}

function GroupHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mt-14 text-2xl font-extrabold tracking-tight text-gray-900 border-b border-gray-200 pb-3">
      {children}
    </h2>
  );
}

export function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="mx-auto max-w-6xl flex items-center justify-between px-6 py-3">
          <Link to="/">
            <img src="/assets/logo/logo-full-dark-text.png" alt="badges.ninja" className="h-9" />
          </Link>
          <Link to="/signup" className="rounded-full bg-primary-600 px-5 py-2 text-xs font-semibold text-white hover:bg-primary-700">
            Sign up free
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-6xl px-6 py-12 sm:py-16">
        <article>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">Terms of Service</h1>
          <p className="mt-3 text-sm text-gray-500">Last updated: April 13, 2026</p>
          <p className="mt-6 text-sm text-gray-600 leading-relaxed">
            These Terms of Service ("Terms") govern your use of the badges.ninja platform operated by BWS ("we", "us", or "our"). By creating an account or using the service, you agree to be bound by these Terms. If you do not agree, do not use the service.
          </p>

          <GroupHeading>What You're Agreeing To When You Use Badges Ninja</GroupHeading>

          <Section id="service-description" title="1. Service Description">
            <p>badges.ninja is a digital badge and credential platform that enables users to create issuers, design badges, and issue verifiable digital credentials to recipients via a web interface and REST API. Each badge issued generates a unique, verifiable credential that can be shared and independently verified.</p>
            <p>The service includes: issuer management, badge design and creation, credential issuance and delivery, badge verification, recipient notification, API key management, user authentication, and subscription management.</p>
          </Section>

          <GroupHeading>Account Responsibilities</GroupHeading>

          <Section id="account" title="2. Account Registration">
            <p>You must create an account to use the service. You may register with an email address and password, or via Google or GitHub authentication. You agree to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Provide accurate and complete registration information</li>
              <li>Keep your login credentials and API keys secure</li>
              <li>Notify us immediately of any unauthorized access to your account</li>
              <li>Accept responsibility for all activity under your account</li>
            </ul>
            <p>We reserve the right to suspend or terminate accounts that violate these Terms.</p>
          </Section>

          <Section id="acceptable-use" title="3. Acceptable Use">
            <p>You agree not to use badges.ninja to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Create or issue fraudulent, fake, or misleading credentials</li>
              <li>Impersonate organizations, institutions, or individuals you do not represent</li>
              <li>Issue badges that falsely claim affiliation with educational institutions, government bodies, or professional organizations</li>
              <li>Send spam or unsolicited credentials to recipients who have not earned them</li>
              <li>Upload badge images or content that is illegal, infringing, defamatory, obscene, or harmful</li>
              <li>Distribute malware, viruses, or any destructive code via badge links or images</li>
              <li>Violate any applicable law, regulation, or third-party rights including intellectual property rights</li>
              <li>Attempt to gain unauthorized access to the service, other accounts, or underlying infrastructure</li>
              <li>Circumvent usage limits, rate limits, or other plan restrictions</li>
              <li>Resell or redistribute the service without our written consent</li>
            </ul>
          </Section>

          <Section id="prohibited-content" title="4. Prohibited Content">
            <p>You may not use badges.ninja to create, issue, or distribute credentials that:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Certify illegal activities or encourage unlawful behavior</li>
              <li>Contain abusive, threatening, or hateful content</li>
              <li>Misrepresent qualifications, skills, or achievements</li>
              <li>Infringe on trademarks, copyrights, or other intellectual property of third parties</li>
              <li>Contain personally identifiable information of individuals without their consent</li>
            </ul>
            <p>We reserve the right to remove any content that violates these restrictions and to suspend accounts responsible for repeated violations.</p>
          </Section>

          <GroupHeading>Badge Issuance and Data Ownership</GroupHeading>

          <Section id="intellectual-property" title="5. Intellectual Property">
            <p><strong>Your content.</strong> You retain ownership of all badge designs, images, and content you create on the platform. By using the service, you grant BWS a non-exclusive, worldwide license to store, display, and serve your badge content for the purpose of providing the service, including badge verification and public credential pages.</p>
            <p><strong>Verification display.</strong> You acknowledge that badges issued through the platform are designed to be publicly verifiable. Badge titles, issuer names, issue dates, and badge images will be displayed on public verification pages to allow third parties to confirm the authenticity of credentials.</p>
            <p><strong>Platform rights.</strong> The badges.ninja platform, including its design, code, and documentation, is the property of BWS. These Terms do not grant you any rights to BWS trademarks, branding, or platform intellectual property.</p>
          </Section>

          <Section id="badge-permanence" title="6. Badge Permanence and Hosting">
            <p><strong>Image hosting.</strong> Badge images are hosted on Amazon S3 and served through our CDN. Each image is stored under a content-addressed, immutable URL, so issued credentials keep showing the same artwork.</p>
            <p><strong>Permanence.</strong> We keep issued badge images available for the life of the credential so recipients and verifiers can rely on them. If you delete a badge or your account, its image is removed from our storage.</p>
            <p><strong>Badge revocation.</strong> While you may revoke a credential within the platform, previously shared verification links and cached badge data may continue to exist outside our control.</p>
          </Section>

          <Section id="plans-billing" title="7. Plans and Billing">
            <p><strong>Free tier.</strong> The free plan includes a limited monthly credit allowance for badge operations. No payment information is required.</p>
            <p><strong>Paid plans.</strong> Paid subscriptions are billed monthly through Stripe. By subscribing, you authorize recurring charges to your payment method. Subscriptions auto-renew at the end of each billing period unless cancelled.</p>
            <p><strong>Credit limits.</strong> Each plan has a monthly credit allowance. Credits reset on the first day of each calendar month. Exceeding your plan's credit limit will prevent new badge operations until the next reset or until you upgrade.</p>
            <p><strong>Cancellation.</strong> You may cancel your subscription at any time through the billing portal. Upon cancellation, your account reverts to the free tier at the end of the billing period. Your existing badges and credentials remain active.</p>
            <p><strong>Refunds.</strong> Subscription fees are non-refundable except where required by applicable law.</p>
          </Section>

          <Section id="api-access" title="8. API Access">
            <p>We provide API access for programmatic badge management, issuance, and verification. API keys are issued per account and must be kept confidential. You are responsible for all API activity performed with your keys. We reserve the right to rate-limit or revoke API access to prevent abuse or protect service stability. Fair use policies apply to all API endpoints.</p>
          </Section>

          <GroupHeading>Service Terms and Liability</GroupHeading>

          <Section id="service-availability" title="9. Service Availability">
            <p>We strive to maintain high availability but do not guarantee uninterrupted service. The service is provided on an "as is" and "as available" basis. We may perform scheduled maintenance, and unplanned outages may occur. We are not liable for any data loss or interruption of service.</p>
          </Section>

          <Section id="disclaimer" title="10. Disclaimer of Warranties">
            <p>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.</p>
            <p>WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT BADGES OR CREDENTIALS WILL BE PERMANENTLY AVAILABLE OR VERIFIABLE.</p>
          </Section>

          <Section id="limitation" title="11. Limitation of Liability">
            <p>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, BWS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR GOODWILL, WHETHER IN CONTRACT, TORT, OR OTHERWISE, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE SERVICE.</p>
            <p>OUR TOTAL LIABILITY FOR ANY CLAIM ARISING FROM OR RELATED TO THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID TO BWS IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.</p>
            <p>BWS is not liable for any decisions made by third parties based on badges or credentials issued through the platform. The platform provides a tool for creating and verifying digital credentials — the accuracy and legitimacy of credential content is the sole responsibility of the issuer.</p>
          </Section>

          <Section id="indemnification" title="12. Indemnification">
            <p>You agree to indemnify, defend, and hold harmless BWS and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, costs, and expenses (including legal fees) arising from:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Your use of the service</li>
              <li>Badges or credentials you create, issue, or distribute through the service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any applicable law or third-party rights</li>
              <li>Claims by badge recipients or third parties related to credentials you issued</li>
            </ul>
          </Section>

          <Section id="takedown" title="13. Copyright and Takedown">
            <p>If you believe content hosted on badges.ninja infringes your copyright or intellectual property rights, please contact us at <a href="mailto:info@bws.ninja" className="text-primary-600 hover:text-primary-500">info@bws.ninja</a> with:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>A description of the copyrighted work</li>
              <li>The URL of the infringing badge or content</li>
              <li>Your contact information</li>
              <li>A statement of good faith belief that the use is unauthorized</li>
            </ul>
            <p>We will remove infringing content, including badge images, from our platform upon valid request. Copies you have already shared publicly are outside our control.</p>
          </Section>

          <GroupHeading>Termination and Refunds</GroupHeading>

          <Section id="termination" title="14. Termination">
            <p>We may suspend or terminate your access to the service at any time, with or without notice, for conduct that we believe violates these Terms, is harmful to other users or the service, or for any other reason at our sole discretion. Upon termination, your right to use the service ceases immediately. Badges already issued and distributed may remain publicly accessible for verification purposes.</p>
          </Section>

          <Section id="modifications" title="15. Modifications to Terms">
            <p>We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on this page with a revised date. Your continued use of the service after changes constitutes acceptance. If you disagree with the updated Terms, you must stop using the service and delete your account.</p>
          </Section>

          <Section id="governing-law" title="16. Governing Law">
            <p>These Terms are governed by and construed in accordance with applicable international law. Any disputes arising from these Terms or the service shall be resolved through good-faith negotiation. If negotiation fails, disputes shall be submitted to binding arbitration.</p>
          </Section>

          <Section id="contact" title="17. Contact">
            <p>For questions about these Terms, contact us at:</p>
            <p className="mt-2">
              <strong>BWS</strong><br />
              Email: <a href="mailto:info@bws.ninja" className="text-primary-600 hover:text-primary-500">info@bws.ninja</a><br />
              Website: <a href="https://www.bws.ninja" className="text-primary-600 hover:text-primary-500" target="_blank" rel="noopener noreferrer">www.bws.ninja</a>
            </p>
          </Section>
        </article>

        {/* Footer nav */}
        <div className="mt-16 pt-8 border-t border-gray-200 flex items-center justify-between text-sm text-gray-500">
          <Link to="/" className="hover:text-gray-900">Back to badges.ninja</Link>
          <Link to="/privacy-policy" className="hover:text-gray-900">Privacy Policy</Link>
        </div>
      </main>
    </div>
  );
}
